import { type FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { useProductProfileProductsLoadQuery } from "src/app/features/ProductProfile/services/server/ProductProfileApi";
import type { ProductProfileProductEntity } from "src/app/features/ProductProfile/types/subTypes/ProductProfileProductEntity";
import { ActionBar } from "../../../ActionBar/ActionBar";
import { SecondaryButton } from "../../../Buttons/components/ActionButton";
import type { ExtendedColumnsType } from "../../../Table";
import { PropertyTable } from "../../../Table/components/PropertyTable";
import { displayEnumValue } from "../../../Table/components/PropertyTable/transformations/cellTransformations";
import { Checkbox } from "../Checkbox/Checkbox";

const getColumnsConfig = (
    t: Function,
    onToggle: (id: string | number, key: keyof ProductProfileProductEntity, value: boolean) => void,
    readOnly: boolean,
): ExtendedColumnsType<ProductProfileProductEntity>[] => {
    return [
        {
            title: t("product.id"),
            key: "productId",
            dataIndex: "productId",
        },
        {
            title: t("product.code"),
            key: "grCode",
            dataIndex: "grCode",
        },
        {
            title: t("product.productCode"),
            key: "productCode",
            dataIndex: "productCode",
        },
        {
            title: t("product.name"),
            key: "name",
            dataIndex: "name",
        },
        {
            title: t("product.shortName"),
            key: "shortName",
            dataIndex: "shortName",
        },
        {
            title: t("product.type"),
            key: "type",
            dataIndex: "type",
            render: (value) => displayEnumValue(value, "product-type"),
        },
        {
            title: t("product.itemType"),
            key: "itemType",
            dataIndex: "itemType",
            render: (value) => displayEnumValue(value, "product-item-type"),
        },
        { title: t("product.vatCode"), key: "vatCode", dataIndex: "vatCode" },
        {
            title: t("product.enabled"),
            key: "enabled",
            dataIndex: "enabled",
            render: (_, record) => {
                const recordId = record.id ?? record.productId;
                return (
                    <Checkbox
                        name={`enabled-${recordId}`}
                        label=""
                        value={record.enabled ?? false}
                        readOnly={readOnly}
                        onChange={(checked) => onToggle(recordId!, "enabled", checked)}
                    />
                );
            },
        },
        {
            title: t("product.mandatory"),
            key: "mandatory",
            dataIndex: "mandatory",
            render: (_, record) => {
                const recordId = record.id ?? record.productId;
                return (
                    <Checkbox
                        name={`mandatory-${recordId}`}
                        label=""
                        value={record.mandatory ?? false}
                        readOnly={!record.enabled || readOnly}
                        onChange={(checked) => onToggle(recordId!, "mandatory", checked)}
                    />
                );
            },
        },
        {
            title: t("product.hiddenInPos"),
            key: "hiddenInPos",
            dataIndex: "hiddenInPos",
            render: (_, record) => {
                const recordId = record.id ?? record.productId;
                return (
                    <Checkbox
                        name={`hidden-${recordId}`}
                        label=""
                        value={record.hiddenInPos ?? false}
                        readOnly={!record.enabled || readOnly}
                        onChange={(checked) => onToggle(recordId!, "hiddenInPos", checked)}
                    />
                );
            },
        },
    ];
};

type ProductProfileProductsTableProps = {
    value?: ProductProfileProductEntity[];
    onChange?: (data: ProductProfileProductEntity[]) => void;
    readOnly?: boolean;
};

export const ProductProfileProductsTable: FC<ProductProfileProductsTableProps> = ({ value, onChange, readOnly }) => {
    const { t } = useTranslation();
    const { id: profileId } = useParams();

    const { data: fetchedProducts } = useProductProfileProductsLoadQuery(profileId ? { productProfileId: profileId } : undefined);

    const safeValue = Array.isArray(value) && value.length > 0 ? value : Array.isArray(fetchedProducts) ? fetchedProducts : [];

    useEffect(() => {
        if (Array.isArray(fetchedProducts) && fetchedProducts.length > 0 && (!Array.isArray(value) || value.length === 0) && onChange) {
            onChange(fetchedProducts);
        }
    }, [fetchedProducts, onChange, value]);

    const onToggle = (rowId: string | number, key: keyof ProductProfileProductEntity, checked: boolean) => {
        const newData = safeValue.map((row) => {
            const currentRowId = row.id ?? row.productId;
            if (currentRowId !== rowId) return row;

            const next: ProductProfileProductEntity = { ...row, [key]: checked };

            if (key === "enabled" && !checked) {
                next.mandatory = false;
                next.hiddenInPos = false;
            }

            if ((key === "mandatory" || key === "hiddenInPos") && checked) {
                next.enabled = true;
            }

            return next;
        });

        onChange?.(newData);
    };

    const enableAll = () => {
        onChange?.(safeValue.map((row) => ({ ...row, enabled: true })));
    };

    const enableAllWithMandat = () => {
        onChange?.(
            safeValue.map((row) => ({
                ...row,
                enabled: true,
                mandatory: true,
                hiddenInPos: false,
            })),
        );
    };

    const enableAllWithHidden = () => {
        onChange?.(
            safeValue.map((row) => ({
                ...row,
                enabled: true,
                hiddenInPos: true,
                mandatory: false,
            })),
        );
    };

    const disableAll = () => {
        onChange?.(
            safeValue.map((row) => ({
                ...row,
                enabled: false,
                mandatory: false,
                hiddenInPos: false,
            })),
        );
    };

    return (
        <>
            <ActionBar className="left">
                <SecondaryButton onClick={enableAll} disabled={readOnly}>
                    {t("button.enableAll")}
                </SecondaryButton>
                <SecondaryButton onClick={enableAllWithMandat} disabled={readOnly}>
                    {t("button.enableAllMandat")}
                </SecondaryButton>
                <SecondaryButton onClick={enableAllWithHidden} disabled={readOnly}>
                    {t("button.enableAllHiddenPOS")}
                </SecondaryButton>
                <SecondaryButton onClick={disableAll} disabled={readOnly}>
                    {t("button.disableAll")}
                </SecondaryButton>
            </ActionBar>

            <PropertyTable<ProductProfileProductEntity>
                rowsAreSelectable={false}
                getColumnsConfig={() => getColumnsConfig(t, onToggle, !!readOnly)}
                propertyAsRowKey="productId"
                tableData={safeValue}
                readOnly={!!readOnly}
                onDataChanged={onChange}
                actions={[]}
            />
        </>
    );
};
